import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import ProLayout from '@/components/layouts/ProLayout';
import { 
  LayoutDashboard, Users, BookOpen, UserCog, Video, BarChart3,
  Gamepad2, FileText, PenTool, Languages, ClipboardList,
  ArrowLeft, User, Mail, Phone, GraduationCap, TrendingUp,
  TrendingDown, Minus, CheckCircle, XCircle, Target, Lightbulb,
  Loader2, Calculator, FlaskConical, UserPlus, Sparkles, Send, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const AssessmentReport = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertData, setConvertData] = useState({
    subjects: ['maths', 'english', 'science'],
    tutor_id: ''
  });
  const [tutors, setTutors] = useState([]);
  const [sendingReport, setSendingReport] = useState(false);

  const sidebarItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/users', label: 'User Management', icon: Users },
    { path: '/admin/allocate', label: 'Subject Allocation', icon: BookOpen },
    { path: '/admin/tutors', label: 'Tutor Assignment', icon: UserCog },
    { path: '/tutoring', label: 'Live Sessions', icon: Video },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/assessments', label: 'Assessments', icon: ClipboardList },
    { divider: true, label: 'Content Preview' },
    { path: '/admin/preview/games', label: 'Games Zone', icon: Gamepad2 },
    { path: '/admin/preview/exams', label: 'Exams', icon: FileText },
    { path: '/admin/preview/essays', label: 'Essays', icon: PenTool },
    { path: '/admin/preview/esol-videos', label: 'ESOL Videos', icon: Languages },
  ];

  useEffect(() => {
    fetchReport();
    fetchTutors();
  }, [submissionId]);

  const fetchReport = async () => {
    try {
      const res = await api.get(`/assessment/report/${submissionId}`);
      setReport(res.data);
      // Pre-fill subjects from parent's choices if available
      if (res.data.parent_subject_choices && res.data.parent_subject_choices.length > 0) {
        setConvertData(prev => ({
          ...prev,
          subjects: res.data.parent_subject_choices
        }));
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load report');
    }
    setLoading(false);
  };

  const fetchTutors = async () => {
    try {
      const res = await api.get('/admin/tutors');
      setTutors(res.data.tutors || []);
    } catch (err) {
      console.log('Could not fetch tutors');
    }
  };

  const handleConvertToStudent = async () => {
    setConverting(true);
    try {
      const res = await api.post(`/assessment/convert-to-student/${submissionId}`, {
        subjects: convertData.subjects,
        tutor_id: convertData.tutor_id || null
      });
      toast.success(`Student account created for ${report.child.name}! Welcome email sent.`);
      setShowConvertModal(false);
      // Navigate to subject allocation page
      navigate(`/admin/allocate?highlight=${res.data.student_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create student');
    }
    setConverting(false);
  };

  const handleSendReportToParent = async () => {
    setSendingReport(true);
    try {
      await api.post(`/assessment/send-report/${submissionId}`);
      toast.success(`Report sent to ${report.parent.email}!`);
      // Refresh to update report_sent_to_parent status
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send report');
    }
    setSendingReport(false);
  };

  const toggleSubject = (subject) => {
    setConvertData(prev => ({
      ...prev,
      subjects: prev.subjects.includes(subject)
        ? prev.subjects.filter(s => s !== subject)
        : [...prev.subjects, subject]
    }));
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 70) return 'text-emerald-600';
    if (percentage >= 50) return 'text-amber-600';
    return 'text-red-500';
  };

  const getScoreBg = (percentage) => {
    if (percentage >= 70) return 'bg-emerald-50';
    if (percentage >= 50) return 'bg-amber-50';
    return 'bg-red-50';
  };

  const getLevelIcon = (level) => {
    if (level === 'above') return <TrendingUp className="w-5 h-5 text-emerald-600" />;
    if (level === 'below') return <TrendingDown className="w-5 h-5 text-red-500" />;
    return <Minus className="w-5 h-5 text-amber-600" />;
  };

  const subjectIcons = {
    maths: Calculator,
    english: BookOpen,
    science: FlaskConical
  };

  const subjectColors = {
    maths: { bg: 'bg-amber-500', light: 'bg-amber-50', text: 'text-amber-600' },
    english: { bg: 'bg-rose-500', light: 'bg-rose-50', text: 'text-rose-600' },
    science: { bg: 'bg-emerald-500', light: 'bg-emerald-50', text: 'text-emerald-600' }
  };

  if (loading) {
    return (
      <ProLayout sidebarItems={sidebarItems} title="Assessment Report">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </ProLayout>
    );
  }

  if (error) {
    return (
      <ProLayout sidebarItems={sidebarItems} title="Assessment Report">
        <div className="bg-red-50 text-red-700 p-6 rounded-xl text-center">
          <p>{error}</p>
          <Link to="/admin/assessments" className="text-red-800 underline mt-2 inline-block">
            Back to Assessments
          </Link>
        </div>
      </ProLayout>
    );
  }

  const { parent, child, completed_at, report: reportData, student_created, parent_subject_choices, report_sent_to_parent } = report;

  return (
    <ProLayout sidebarItems={sidebarItems} title="Assessment Report">
      {/* Convert to Student Modal */}
      {showConvertModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">Convert to Student</h3>
                <p className="text-sm text-slate-500">Create account for {child.name}</p>
              </div>
            </div>
            
            <div className="space-y-4 mb-6">
              {/* Child Info Summary */}
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-slate-600"><strong>Child:</strong> {child.name}</p>
                <p className="text-sm text-slate-600"><strong>Year Group:</strong> Year {child.year_group}</p>
                <p className="text-sm text-slate-600"><strong>Parent:</strong> {parent.name} ({parent.email})</p>
              </div>

              {/* Subject Allocation */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Allocate Subjects
                  {report?.parent_subject_choices && (
                    <span className="ml-2 text-blue-600 font-normal">(Pre-filled from parent's selection)</span>
                  )}
                </label>
                <div className="flex gap-2">
                  {['maths', 'english', 'science'].map(subject => (
                    <button
                      key={subject}
                      onClick={() => toggleSubject(subject)}
                      className={`px-4 py-2 rounded-lg font-medium capitalize transition-all ${
                        convertData.subjects.includes(subject)
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {subject}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tutor Assignment (Optional) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Assign Tutor (Optional)</label>
                <select
                  value={convertData.tutor_id}
                  onChange={(e) => setConvertData({ ...convertData, tutor_id: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">No tutor assigned</option>
                  {tutors.map(tutor => (
                    <option key={tutor.id} value={tutor.id}>
                      {tutor.first_name} {tutor.last_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Recommendations Note */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Recommended Starting Point:</strong> Year {reportData.overall_recommended_year}
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  Based on assessment results, the student should start with Year {reportData.overall_recommended_year} curriculum.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowConvertModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConvertToStudent}
                disabled={converting || convertData.subjects.length === 0}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {converting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Student Account'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Back Link */}
      <Link 
        to="/admin/assessments"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-emerald-600 font-medium mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Assessments
      </Link>

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 mb-1">
              Assessment Report: {child.name}
            </h1>
            <p className="text-slate-500">
              Completed on {new Date(completed_at).toLocaleDateString('en-GB', {
                day: 'numeric', month: 'long', year: 'numeric'
              })}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {student_created ? (
              <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-xl">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Student Account Created</span>
              </div>
            ) : (
              <>
                {/* Send Report Button */}
                {!report_sent_to_parent ? (
                  <Button
                    onClick={handleSendReportToParent}
                    disabled={sendingReport}
                    variant="outline"
                    className="flex items-center gap-2 border-blue-300 text-blue-600 hover:bg-blue-50"
                    data-testid="send-report-to-parent"
                  >
                    {sendingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Send Report to Parent
                  </Button>
                ) : !parent_subject_choices ? (
                  <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-xl">
                    <Clock className="w-5 h-5" />
                    <span className="font-medium text-sm">Awaiting Parent's Subject Selection</span>
                  </div>
                ) : null}
                
                {/* Convert Button - show if parent has selected subjects OR if report not sent yet */}
                <Button
                  onClick={() => setShowConvertModal(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2"
                  data-testid="convert-to-student"
                >
                  <UserPlus className="w-4 h-4" />
                  Convert to Student
                </Button>
              </>
            )}
            <div className={`px-4 py-2 rounded-xl ${getScoreBg(reportData.overall_percentage)}`}>
              <p className="text-sm text-slate-600">Overall Score</p>
              <p className={`text-3xl font-bold ${getScoreColor(reportData.overall_percentage)}`}>
                {reportData.overall_percentage}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact & Child Info */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
          <h3 className="font-semibold text-slate-800 mb-4">Parent/Guardian Details</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-slate-400" />
              <span className="text-slate-700">{parent.name}</span>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-slate-400" />
              <a href={`mailto:${parent.email}`} className="text-emerald-600 hover:underline">{parent.email}</a>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-slate-400" />
              <a href={`tel:${parent.phone}`} className="text-emerald-600 hover:underline">{parent.phone}</a>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
          <h3 className="font-semibold text-slate-800 mb-4">Child's Details</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-slate-400" />
              <span className="text-slate-700">{child.name}</span>
            </div>
            <div className="flex items-center gap-3">
              <GraduationCap className="w-5 h-5 text-slate-400" />
              <span className="text-slate-700">Current Year Group: <strong>Year {child.year_group}</strong></span>
            </div>
            <div className="flex items-center gap-3">
              <Target className="w-5 h-5 text-slate-400" />
              <span className="text-slate-700">
                Recommended Starting Point: <strong className="text-emerald-600">Year {reportData.overall_recommended_year}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Parent's Subject Choices - shown if parent has submitted their choices */}
      {parent_subject_choices && parent_subject_choices.length > 0 && (
        <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl p-6 mb-6 text-white">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg mb-2">Parent's Subject Selection</h3>
              <p className="text-blue-100 mb-3">The parent has chosen the following subjects for {child.name}:</p>
              <div className="flex gap-2 flex-wrap">
                {parent_subject_choices.map(subject => (
                  <span key={subject} className="px-4 py-2 bg-white/20 rounded-lg font-medium capitalize">
                    {subject}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Starting Point Recommendation */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-6 mb-6 text-white">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Target className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-bold text-lg mb-2">Starting Point Recommendation</h3>
            <p className="text-emerald-50">{reportData.starting_point}</p>
          </div>
        </div>
      </div>

      {/* Subject Breakdown */}
      <h2 className="font-bold text-xl text-slate-800 mb-4">Subject Analysis</h2>
      <div className="grid md:grid-cols-3 gap-6 mb-6">
        {Object.entries(reportData.subjects).map(([subject, data]) => {
          const Icon = subjectIcons[subject];
          const colors = subjectColors[subject];
          
          return (
            <div key={subject} className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
              <div className={`${colors.bg} p-4 flex items-center gap-3`}>
                <Icon className="w-6 h-6 text-white" />
                <h3 className="font-bold text-white capitalize">{subject}</h3>
              </div>
              <div className="p-5">
                {/* Score */}
                <div className="text-center mb-4">
                  <p className={`text-4xl font-bold ${getScoreColor(data.percentage)}`}>
                    {data.percentage}%
                  </p>
                  <p className="text-sm text-slate-500">{data.correct}/{data.total} correct</p>
                </div>

                {/* Level Assessment */}
                <div className={`${colors.light} rounded-lg p-3 mb-4 flex items-center justify-between`}>
                  <span className="text-sm font-medium text-slate-700">Recommended Level</span>
                  <span className={`font-bold ${colors.text}`}>Year {data.recommended_year}</span>
                </div>

                {/* Difficulty Breakdown */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Easy Questions</span>
                    <span className="font-medium">{data.easy_score}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div 
                      className="bg-emerald-500 h-2 rounded-full"
                      style={{ width: `${data.easy_score}%` }}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Medium Questions</span>
                    <span className="font-medium">{data.medium_score}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div 
                      className="bg-amber-500 h-2 rounded-full"
                      style={{ width: `${data.medium_score}%` }}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Hard Questions</span>
                    <span className="font-medium">{data.hard_score}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div 
                      className="bg-purple-500 h-2 rounded-full"
                      style={{ width: `${data.hard_score}%` }}
                    />
                  </div>
                </div>

                {/* Strengths & Weaknesses */}
                {data.strengths.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-emerald-600 mb-1 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Strengths
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {data.strengths.map((s, i) => (
                        <span key={i} className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {data.weaknesses.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-red-500 mb-1 flex items-center gap-1">
                      <XCircle className="w-3 h-3" /> Areas to Develop
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {data.weaknesses.map((w, i) => (
                        <span key={i} className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                          {w}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detailed Recommendations */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-amber-600" />
          </div>
          <h3 className="font-bold text-lg text-slate-800">Detailed Recommendations</h3>
        </div>
        
        <div className="space-y-3">
          {reportData.recommendations.map((rec, i) => (
            <div 
              key={i} 
              className={`p-3 rounded-lg ${rec.startsWith('**') ? 'bg-slate-50' : 'bg-slate-25 ml-4'}`}
            >
              <p className="text-slate-700" dangerouslySetInnerHTML={{ 
                __html: rec.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
              }} />
            </div>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="bg-slate-50 rounded-xl p-6">
        <h3 className="font-semibold text-slate-800 mb-4">Assessment Summary</h3>
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-slate-800">
              {reportData.summary.total_correct}/{reportData.summary.total_questions}
            </p>
            <p className="text-sm text-slate-500">Total Correct</p>
          </div>
          <div className="bg-white rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">
              {reportData.summary.strengths_count}
            </p>
            <p className="text-sm text-slate-500">Strength Areas</p>
          </div>
          <div className="bg-white rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-red-500">
              {reportData.summary.weaknesses_count}
            </p>
            <p className="text-sm text-slate-500">Areas to Develop</p>
          </div>
          <div className="bg-white rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">
              Year {reportData.overall_recommended_year}
            </p>
            <p className="text-sm text-slate-500">Starting Point</p>
          </div>
        </div>
      </div>
    </ProLayout>
  );
};

export default AssessmentReport;
